import React from "react";
import { useHistory, useLocation } from "react-router";

type ParamKeyValuePair = [string, string];

interface FullURLSearchParams extends URLSearchParams {
  keys(): IterableIterator<string>;
}

type URLSearchParamsInit = string | ParamKeyValuePair[] | Record<string, string | string[]> | FullURLSearchParams;

/**
 * A convenient wrapper for reading and writing search parameters via the URLSearchParams interface.
 *
 * based on react-router v6 function
 *
 * @see https://reactrouter.com/docs/en/v6/hooks/use-search-params
 */
export const useSearchParams = (defaultInit?: URLSearchParamsInit) => {
  const defaultSearchParamsRef = React.useRef(createSearchParams(defaultInit));

  const location = useLocation();
  const searchParams = React.useMemo(() => {
    const params = createSearchParams(location.search);

    for (const key of defaultSearchParamsRef.current.keys()) {
      if (!params.has(key)) {
        defaultSearchParamsRef.current.getAll(key).forEach((value) => {
          params.append(key, value);
        });
      }
    }

    return params;
  }, [location.search]);

  const history = useHistory();
  const setSearchParams = React.useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (nextInit: URLSearchParamsInit, navigateOptions?: { replace?: boolean; state?: any }) => {
          history.push("?" + createSearchParams(nextInit), navigateOptions);
      },
      [history]
  );

  return [searchParams, setSearchParams] as const;
};

/**
 * Creates a URLSearchParams object using the given initializer.
 *
 * This is identical to `new URLSearchParams(init)` except it also supports arrays as values in the object form of the initializer
 * instead of just strings. This is convenient when you need multiple values for a given key, but don't want to use an array initializer.
 *
 * For example, instead of:
 *
 *   let searchParams = new URLSearchParams([
 *     ['sort', 'name'],
 *     ['sort', 'price']
 *   ]);
 *
 * you can do:
 *
 *   let searchParams = createSearchParams({
 *     sort: ['name', 'price']
 *   });
 *
 * based on react-router v6 function
 *
 * @see https://reactrouter.com/docs/en/v6/utils/create-search-params
 */
export const createSearchParams = (init: URLSearchParamsInit = ""): FullURLSearchParams =>
  new URLSearchParams(
    typeof init === "string" || Array.isArray(init) || init instanceof URLSearchParams
      ? init
      : Object.keys(init).reduce((memo, key) => {
          const value = init[key];
          return memo.concat(Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]);
        }, [] as ParamKeyValuePair[])
  ) as FullURLSearchParams;
